import React, { useCallback, useRef } from 'react';
import { useAccessGate } from '../contexts/AccessGateContext';
import { isFanvueProfileUrl } from '../utils/fanvue';
import { trackCTAHover } from '../utils/metaPixel';
interface ButtonProps {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
  className?: string;
}
export function Button({
  children,
  href,
  onClick,
  variant = 'primary',
  className = ''
}: ButtonProps) {
  const {
    openAccessGate
  } = useAccessGate();
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTrackedHoverRef = useRef(false);
  const baseStyles = 'group relative inline-flex items-center justify-center px-10 py-5 text-sm font-medium tracking-widest uppercase transition-all duration-500 rounded-sm overflow-hidden';
  const variantStyles = {
    primary: 'bg-gradient-to-r from-[#D4AF37] via-[#C5A028] to-[#D4AF37] bg-size-200 bg-pos-0 hover:bg-pos-100 text-white shadow-lg shadow-[#D4AF37]/30 hover:shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105 hover:-translate-y-1',
    secondary: 'border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white hover:shadow-lg hover:shadow-[#D4AF37]/30 hover:scale-105'
  };
  const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${className}`;
  const isFanvueCta = Boolean(href && isFanvueProfileUrl(href));
  const handleMouseEnter = useCallback(() => {
    if (isFanvueCta && !hasTrackedHoverRef.current) {
      hoverTimerRef.current = setTimeout(() => {
        trackCTAHover();
        hasTrackedHoverRef.current = true;
      }, 1500);
    }
  }, [isFanvueCta]);
  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (isFanvueCta) {
      openAccessGate(event.currentTarget);
      return;
    }
    onClick?.();
  };
  return <button type="button" className={combinedClassName} onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-69" data-mp="1d8u6uh47qg3k">
      <span className="relative z-10 flex items-center gap-2 transition-transform duration-300 group-hover:gap-3" data-id="element-70" data-mp="13c911tkdd0yo">
        {children}
      </span>
      {variant === 'primary' && <>
          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" data-id="element-71" data-mp="1vzv7raow8lpr" />
          <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-72" data-mp="1mb3kvgyowrlp">
            <span className="absolute top-0 left-1/4 h-2 w-2 rounded-full bg-white/40 animate-sparkle" data-id="element-73" data-mp="2jqr4mm87i73v" />
            <span className="absolute top-1/2 right-1/4 h-1 w-1 rounded-full bg-white/40 animate-sparkle-delay" data-id="element-74" data-mp="2ey7i2zrjk1mx" />
          </span>
        </>}
    </button>;
}